import { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  Paper,
  ButtonBase,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha } from "@mui/material/styles";
import AuthContext from "../../../contexts/AuthContext";
import request from "../../../utilities/requests";

const cardSx = (isSelected, submitting) => (theme) => {
  const base = {
    display: "flex",
    alignItems: "center",
    gap: 2.5,
    px: 2.5,
    py: 3,
    minHeight: 116,
    textAlign: "left",
    borderRadius: 4,
    width: "100%",
    userSelect: "none",
    outline: "none",
    border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    transition:
      "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[3],
    },
    "&:active": {
      transform: "translateY(0px) scale(0.99)",
    },
    "&:focus-visible": {
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}`,
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover, &:active": { transform: "none" },
    },
    cursor: submitting ? "default" : "pointer",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  };

  if (!isSelected) return base;

  return {
    ...base,
    background: `linear-gradient(180deg, ${alpha(
      theme.palette.success.light,
      0.35
    )} 0%, ${alpha(theme.palette.success.light, 0.15)} 100%)`,
    borderColor: alpha(theme.palette.success.main, 0.35),
    boxShadow: theme.shadows[4],
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[6],
      background: `linear-gradient(180deg, ${alpha(
        theme.palette.success.main,
        0.28
      )} 0%, ${alpha(theme.palette.success.light, 0.22)} 100%)`,
    },
  };
};

const Ballot = ({ eventId }) => {
  const { socket } = useContext(AuthContext);
  const [round, setRound] = useState(null);
  const [suitors, setSuitors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const seq = useRef(0);

  const getSuitors = useCallback(async () => {
    if (!eventId) return;
    const mine = ++seq.current;
    try {
      setError("");
      const result = await request(`dealbreakers/vote?eventId=${eventId}`);
      if (mine !== seq.current) return;
      if (result?.success) {
        setSuitors(Array.isArray(result.suitors) ? result.suitors : []);
        setRound(result.round);
        setSelectedId(result?.vote?.suitor || null);
      } else {
        setSuitors([]);
        if (result?.canVote !== false) {
          setError(result?.error || "Unable to load the contestants.");
        }
      }
    } catch (e) {
      if (mine !== seq.current) return;
      setSuitors([]);
      setError("Unable to load the contestants.");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    getSuitors();
  }, [getSuitors]);

  useEffect(() => {
    if (!socket || !eventId) return;

    const join = () => {
      socket.emit("joinEvent", eventId);
      getSuitors();
    };

    const refreshBallot = () => {
      setSelectedId(null);
      getSuitors();
    };

    socket.emit("joinEvent", eventId);
    socket.on("connect", join);
    socket.on("updateBallot", refreshBallot);

    return () => {
      socket.off("connect", join);
      socket.off("updateBallot", refreshBallot);
    };
  }, [socket, eventId, getSuitors]);

  const handleVote = async (suitorId) => {
    if (submitting) return;
    const previous = selectedId;
    setSubmitting(true);
    setError("");
    setSelectedId(suitorId);
    const res = await request("dealbreakers/vote", "post", {
      eventId,
      round,
      suitorId,
    });
    if (!res?.success) {
      setSelectedId(previous);
      setError(res?.error || "Could not submit your vote.");
    }
    setSubmitting(false);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            height: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!suitors?.length) {
      return (
        <Box
          sx={{
            height: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography>Voting will open when they are on stage.</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ width: "100%" }}>
        {suitors.map((s) => {
          const id = s?._id;
          const img = s?.profile?.profileImage;
          const name = s?.profile?.firstName || "";
          const isSelected = Boolean(selectedId && id && selectedId === id);
          return (
            <ButtonBase
              key={id}
              onClick={() => handleVote(id)}
              disabled={submitting}
              sx={{ width: "100%", borderRadius: 3 }}
            >
              <Paper
                elevation={isSelected ? 4 : 1}
                sx={cardSx(isSelected, submitting)}
              >
                <Avatar
                  src={img || undefined}
                  alt={name}
                  sx={{
                    width: 76,
                    height: 76,
                    flexShrink: 0,
                    fontSize: 30,
                    ...(isSelected && {
                      border: "3px solid",
                      borderColor: "success.main",
                    }),
                  }}
                >
                  {!img && name ? name.charAt(0) : null}
                </Avatar>

                <Box sx={{ minWidth: 0, textAlign: "left" }}>
                  <Typography variant="h5" fontWeight={800} noWrap>
                    {name}
                  </Typography>
                  {isSelected ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "success.main",
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        Your vote
                      </Typography>
                      <CheckCircleIcon fontSize="small" aria-hidden />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Tap to vote
                    </Typography>
                  )}
                </Box>
              </Paper>
            </ButtonBase>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
      }}
    >
      <Box sx={{ textAlign: "center", pt: 2, pb: 1 }}>
        <Typography variant="h4" fontWeight={800} lineHeight={1.15}>
          Who won that round?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Tap once to vote, no confirm needed
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {renderContent()}

      {suitors?.length ? (
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ mt: "auto", pt: 4, pb: 2 }}
        >
          Changed your mind? Tap someone else.
        </Typography>
      ) : null}
    </Box>
  );
};

export default Ballot;
